import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Alert, Box, HStack, InlineAlert } from '@fhs-legacy/universal-components';
import { IServerOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionLink } from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { MaxCartTotalAlert } from 'components/max-cart-total-alert';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useCartLimit } from 'hooks/use-cart-limit';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { useCartContext } from 'state/cart';
import { useCRMEventsContext } from 'state/crm-events';
import { OrderStatus, useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';
import { useFlag } from 'utils/launchdarkly';
import { routes } from 'utils/routing';
import { isCatering, isDelivery } from 'utils/service-mode';
import { CART_PAGE_CONTINUE_BUTTON } from 'utils/test-ids';

import { CtaWrapper, EnsureBottomPadding, SectionContainer } from '../styled';

import DeliveryCancellationPolicy from './delivery-cancellation-policy';
import SignUpToContinueActionLink from './sign-up-to-continue-action-link';
import { SignUpToContinueDisclaimer } from './styled';
import { CustomScrollViewProps } from './types';
import { logCartContinue } from './utils';

export interface IPaymentNewProps {
  disabled?: boolean;
  offerIsValid: boolean;
  orderStatus: OrderStatus | null;
  serverOrder: IServerOrder | null;
  shouldSkipPriceOrder?: boolean;
  shouldContinue: boolean;
  setIsBadContinueAttempt: React.Dispatch<React.SetStateAction<boolean>>;
  cartFullPageScreenRef?: CustomScrollViewProps;
}

const Payment: React.FC<React.PropsWithChildren<IPaymentNewProps>> = ({
  disabled = false,
  offerIsValid,
  serverOrder,
  shouldSkipPriceOrder,
  shouldContinue,
  setIsBadContinueAttempt,
  cartFullPageScreenRef,
}) => {
  useRumPageView('payment', 'Payment');
  const { formatCurrencyForLocale } = useUIContext();
  const { serviceMode, cartEntries, continueButtonDisabled } =
    useCartContext();
  const order = useOrderContext();

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);

  const { formatMessage } = useIntl();

  const checkoutDeliveryPriceMinimum = useFlag('override-checkout-delivery-minimum');
  const checkoutCateringPriceMinimum = useFlag('override-checkout-catering-minimum');

  const isSignUpAfterCart = useIsSignUpAfterCart();

  const isCateringOrder = isCatering(serviceMode);
  const isDeliveryOrder = isDelivery(serviceMode);

  const checkoutPriceMinimum = isDeliveryOrder ? checkoutDeliveryPriceMinimum : 0;

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? order.calculatedCartTotal
      : serverOrder?.cart?.subTotalCents;
  const donationAmount = cartEntries.find(entry => entry.isDonation);
  const cartPriceTooLow = cartSubTotal - (donationAmount?.price ?? 0) < checkoutPriceMinimum;

  const { getErrorMessages } = useGetPaymentErrors({ cartPriceTooLow });

  const enableDelivery = useFlag('enable-delivery');

  const { logRBIEvent } = useCRMEventsContext();

  const handleContinuePress = () => {
    if (shouldContinue) {
      logCartContinue(logRBIEvent);
    } else {
      setIsBadContinueAttempt(true);
      cartFullPageScreenRef?.scrollTo({ y: 0 });
    }
  };

  const errorMessage = getErrorMessages({
    invalidOfferError: !offerIsValid,
    cartPriceTooLow,
  });

  const hasError = errorMessage.filter(error => error.messageId !== 'minimumPriceError').length > 0;

  const { isOverCartLimit } = useCartLimit();


  const continueButton = hasError ? (
    <ActionButton
      onPress={() => {
        logCartContinue(logRBIEvent);
        setIsCheckoutErrorsModalOpen(true);
      }}
      fullWidth
      noLogOnLongLoading
      disabled={disabled || !offerIsValid || cartPriceTooLow || shouldSkipPriceOrder}
      isLoading={order.reorder.loadingEnrichCartEntries}
    >
      {formatMessage({ id: 'continue' })}
    </ActionButton>
  ) : (
    <ActionLink
      onPress={handleContinuePress}
      to={shouldContinue ? routes.cartPayment : ''}
      fullWidth
      noLogOnLongLoading
      testID={CART_PAGE_CONTINUE_BUTTON}
      disabled={
        disabled ||
        cartPriceTooLow ||
        isOverCartLimit ||
        continueButtonDisabled ||
        cartSubTotal === undefined ||
        cartSubTotal === null ||
        order.reorder.loadingEnrichCartEntries
      }
      isLoading={
        cartSubTotal === undefined ||
        cartSubTotal === null ||
        order.reorder.loadingEnrichCartEntries
      }
    >
      {formatMessage({ id: 'continue' })}
    </ActionLink>
  );

  const showAlertsBasedOnTotal =
    cartSubTotal !== undefined && cartSubTotal !== null && cartPriceTooLow;

  return (
    <SectionContainer testID="cart-payment">
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={errorMessage}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      <EnsureBottomPadding>
        {(isDeliveryOrder || isCateringOrder) && showAlertsBasedOnTotal && (
          <InlineAlert
            status="error"
            borderRadius={8}
            marginBottom="$2"
            message={formatMessage(
              {
                id: isCateringOrder ? 'cateringMinimumNotReached' : 'deliveryMinumumNotReached',
              },
              {
                min: isCateringOrder
                  ? formatCurrencyForLocale(checkoutCateringPriceMinimum)
                  : formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
              }
            )}
          />
        )}
        {isCateringOrder
          ? null
          : showAlertsBasedOnTotal && (
              <InlineAlert
                status="error"
                borderRadius={8}
                marginBottom="$2"
                message={formatMessage({ id: 'addSomethingExtra' })}
              />
            )}
        {isOverCartLimit && (
          <Box
            backgroundColor="token.background-inline-alert-error"
            p={{
              base: '$2.5',
              desktop: '$3',
            }}
            borderRadius="sm"
            mb="$5"
          >
            <HStack alignItems="center" space="$2">
              <Alert.Icon />
              <MaxCartTotalAlert isMaxCartTotalBold fontSize={{ base: 'xs', desktop: 'md' }} />
            </HStack>
          </Box>
        )}
        {!isDeliveryOrder || (enableDelivery && serviceMode) ? (
          <CtaWrapper>
            {isSignUpAfterCart ? (
              <Box>
                <SignUpToContinueDisclaimer>
                  {formatMessage({ id: 'signUpToContinueDisclaimer' })}
                </SignUpToContinueDisclaimer>
                <SignUpToContinueActionLink />
              </Box>
            ) : (
              continueButton
            )}
            {isDeliveryOrder && enableDelivery && <DeliveryCancellationPolicy />}
          </CtaWrapper>
        ) : (
          <Box marginTop="$4">
            <ActionButton disabled>delivery coming soon</ActionButton>
          </Box>
        )}
      </EnsureBottomPadding>
    </SectionContainer>
  );
};

export default Payment;
